import React, { useState, useMemo, useEffect, useContext } from 'react';

import { Table } from 'antd';

import styled from 'styled-components';

import { getMoment, getNow, showDateNoTime } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import useCountryName from '@hooks/CachedLists/UseCountryName';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import CustomerDrawer from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawer';
import Money from '@components/Gui/Money';
import { NwAddButton, NwButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import InvoiceProjectsList from '@components/InvoicesAndCreditNotes/Common/InvoiceProjectsList';
import ProjectDrawer from '@components/Project/ProjectDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const SiderContent = styled.div`
    width: 460px;
    border-right: 10px solid ${(props) => props.theme.LayoutBodyBackground};
    font-size: ${(props) => props.theme.FontSizeSm};
    padding-left: 1rem;

    .company-name {
        font-size: 18px;
        font-weight: bold;
        margin: 1rem 0 0 0;
    }

    .contact-info {
        display: flex;
        flex-direction: row;
        margin: 1rem 0 0 0;
        .contact-detail {
            margin-left: 1rem;
            overflow-wrap: break-word;
        }
    }

    .client-info {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;

        .client-left {
            flex: 1 1 0;
        }
        .client-right {
            flex: 1 1 0;
        }
    }

    .projects-list {
        margin-top: 1rem;
    }
    .title {
        font-weight: bold;
        margin-bottom: 0.5rem;
    }
    .ant-btn.ant-btn-link {
        white-space: wrap;
        text-align: left;
    }

    .invoice-top-box {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex: 1 1 0;

        .invoice-top-box-icon {
            min-width: 3rem;
            text-align: center;
            font-size: 1.25rem;
        }

        .invoice-top-box-content {
            .invoice-top-box-content-title {
                font-weight: bold;
                font-size: ${(props) => props.theme.FontSizeSm};
            }
            .invoice-top-box-content-label {
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
            }
            p {
                margin: 0;

                &.overdue {
                    background-color: ${(props) => props.theme.DangerColor};
                    color: #fff;
                    padding: 0.2rem 0.5rem;
                }
            }
            small {
                max-width: 13rem;
                font-size: ${(props) => props.theme.FontSizeSm};
                display: block;
                opacity: 0.5;
                line-height: 1.1em;
            }
        }
    }

    .confirmation-payment {
        margin-top: 1rem;
        display: flex;
        flex-direction: row;
        width: 100%;
    }

    .terms-and-bank-account {
        display: flex;
        flex-direction: row;
        width: 100%;
    }

    .ant-table {
        .ant-table-container {
            .ant-table-content {
                .ant-table-tbody {
                    tr.ant-table-row {
                        td.ant-table-cell {
                            padding: 0.5rem 0.5rem 1rem 0.25rem;
                            text-align: right;
                            p {
                                margin: 0;
                            }

                            &:first-child {
                                text-align: left;
                                padding-right: 0.25rem;
                            }

                            div.create-button-wrapper {
                                padding-top: 0.25rem;
                            }
                        }
                    }
                }
            }
        }
    }
`;

const CreditNotePageSider = ({ invoiceData, creditNoteData, onCreateTransaction }) => {
    const [projectPreviewID, setProjectPreviewID] = useState();
    const [dataSource, setDataSource] = useState([]);
    const { GetFamilyName } = useContext(ListContext);
    const configurations = useConfigurations();

    const handleOpenProjectDrawer = (projectId) => {
        setProjectPreviewID(projectId);
    };

    const handleClosePreviewDrawer = () => {
        setProjectPreviewID();
    };

    const projects = useMemo(() => {
        const projects = [];
        if (invoiceData) {
            const { Rows } = invoiceData;
            if (Rows)
                Rows.map((row) => {
                    const { Statement } = row;
                    if (Statement && Statement.Transaction && Statement.Transaction.Project) {
                        const project = Statement.Transaction.Project;
                        if (project.ID) {
                            if (!projects.find((el) => el.ID === project.ID)) projects.push(project);
                        }
                    }
                    return null;
                });
        }
        return projects;
    }, [invoiceData]);

    const paymentStatusOption = [
        { label: 'NOT PAID', value: 'NotPaid', icon: byPrefixAndName.fasl['hourglass-empty'] },
        {
            label: 'PARTIALLY PAID',
            value: 'PartiallyPaid',
            icon: byPrefixAndName.fasl['envelope-open-dollar'],
        },
        { label: 'PAID', value: 'Paid', icon: byPrefixAndName.fasl['money-check-dollar'] },
    ];

    const handleCreateTransaction = (transactionID, statementID, transactionFamily) => {
        onCreateTransaction(transactionID, statementID, transactionFamily);
    };

    let selectedOption = paymentStatusOption.find((item) => item.value === invoiceData.PaymentStatusName);
    if (!selectedOption) {
        selectedOption = paymentStatusOption[0];
    }

    const invoiceDataPaymentDate = invoiceData.PaymentDate ? invoiceData.PaymentDate : null;
    const invoiceDataPaymentDueDate = invoiceData.PaymentDueDate ? invoiceData.PaymentDueDate : null;

    const translateFamilyName = (record) => {
        const familyCode = record.Transaction.Family;
        const familyName = GetFamilyName(familyCode);
        switch (familyCode) {
            case 'JobFee':
                if (record.isCommission) {
                    return 'Agency Fee';
                }
                if (record.isClientAdvance) {
                    return 'Client Advance';
                }
                return familyName;
            default:
                return familyName;
        }
    };

    const isOverDue = () => {
        //if invoice is not paid and payment due date is in the past
        return !isPaid() && getMoment(invoiceDataPaymentDueDate).isBefore(getNow(), 'day');
    };

    const isPaid = () => {
        return invoiceData.PaymentStatusName === 'Paid';
    };

    useEffect(() => {
        // Converted amounts (*Def) only on invoices in the default currency, as in InvoicePageRows
        const isDefaultCurrency = invoiceData.Currency === configurations.defaultCurrency;
        const noTextRows = invoiceData.Rows.filter((row) => {
            if (!row.Statement) return false;
            return true;
        });
        const _dataSource = noTextRows.map((item, index) => {
            const statement = item.Statement;
            const amount = isDefaultCurrency && statement.AmountDef ? statement.AmountDef : statement.Amount;
            let taxes = [];
            const {
                VatAmount,
                VatCode,
                VatDef,
                VatPerc,
                TaxAmount,
                TaxCode,
                TaxDef,
                TaxPerc,
                TaxAmount2,
                TaxCode2,
                TaxDef2,
                TaxPerc2,
            } = statement;
            if (statement.TaxPerc)
                taxes.push({
                    Type: 'Tax',
                    Amount: isDefaultCurrency && TaxDef ? TaxDef : TaxAmount,
                    Code: TaxCode,
                    Perc: TaxPerc,
                });
            if (statement.TaxPerc2)
                taxes.push({
                    Type: 'Tax',
                    Amount: isDefaultCurrency && TaxDef2 ? TaxDef2 : TaxAmount2,
                    Code: TaxCode2,
                    Perc: TaxPerc2,
                });
            if (statement.VatPerc)
                taxes.push({
                    Type: 'Vat',
                    Amount: isDefaultCurrency && VatDef ? VatDef : VatAmount,
                    Code: VatCode,
                    Perc: VatPerc,
                });
            return {
                key: index,
                index: item.ID,
                Guid: item.GroupGuid,
                Description: statement.Description,
                Text: item.Text,
                Amount: amount,
                Taxes: taxes,
                checked: false,
                disabled: false,
                Type: 'Statement',
                ModelID: statement.Transaction ? statement.Transaction.ModelID : null,
                ModelFullName:
                    statement.Transaction && statement.Transaction.ModelID
                        ? statement.Transaction.ModelName + ' ' + statement.Transaction.ModelSurname
                        : null,
                Transaction: statement.Transaction,
                isCommission: statement.isCommission,
                isClientAdvance: statement.isClientAdvance,
                RefDate: statement.RefDate,
                AccountCode: statement.AccountCode,
            };
        });
        setDataSource([..._dataSource]);
    }, [invoiceData]);

    const isInCreditNote = (record) => {
        return creditNoteData.Rows.some((r) => r.Statement.LinkedInvoiceStatementID === record.index);
    };

    const columns = [
        {
            title: <div style={{ marginLeft: '2rem' }}>Description</div>,
            dataIndex: 'Description',
            key: 'Description',
            className: 'model-name-cell',
            //width: 320,
            render: (_, record) => {
                return (
                    <>
                        <div className="model-name-cell-wrapper">
                            <div className="model-name-and-family">
                                {record.ModelFullName && invoiceData.TypeName !== 'Model' && (
                                    <p>
                                        <strong>{record.ModelFullName.toUpperCase()}</strong>
                                    </p>
                                )}
                                {record.Transaction && record.Transaction.Family && (
                                    <small>{translateFamilyName(record)}</small>
                                )}
                            </div>
                        </div>
                        <div className="row-description-cell-wrapper">
                            {record.Transaction && (
                                <p>
                                    {showDateNoTime(record.RefDate)} - <strong>{record.Transaction.Title}</strong>
                                </p>
                            )}
                            {record.Text ? (
                                <p>
                                    <em>{record.Text}</em>
                                </p>
                            ) : (
                                <p>
                                    <em style={{ opacity: 0.6 }}>No text</em>
                                </p>
                            )}
                            {/* {enableCOA &&
                                <Text
                                    code
                                    onClick={locked ? null : () => { changeAccountCode(record)}}
                                    style={{cursor: locked ? "default" : "pointer"}}
                                >
                                    account #: {record.AccountCode ? record.AccountCode : 'n.d.'}
                                </Text>
                            } */}
                        </div>
                        <div className="create-button-wrapper">
                            <NwAddButton
                                primary
                                size="small"
                                disabled={record.Amount <= 0 || isInCreditNote(record)}
                                label="add to credit note"
                                onClick={() =>
                                    handleCreateTransaction(
                                        record.Transaction.ID,
                                        record.index,
                                        record.Transaction.Family
                                    )
                                }
                            />
                        </div>
                    </>
                );
            },
        },
        {
            title: 'Taxes',
            dataIndex: 'Taxes',
            key: 'Taxes',
            width: 130,
            render: (_, record) => (
                <>
                    <div className="invoice-row-amount">
                        <Money
                            amount={record.Amount}
                            currency={invoiceData.Currency}
                            //invert={invertAmount}
                        />
                    </div>
                    <div className="invoice-row-taxes">
                        {record.Taxes.map((tax, index) => (
                            <div className="invoice-row-tax" key={index}>
                                <small>
                                    {tax.Code} ({tax.Perc}%)
                                </small>
                                <div>
                                    <Money
                                        amount={tax.Amount}
                                        //invert={invertAmount}
                                        currency={invoiceData.Currency}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ),
        },
    ];

    return (
        <>
            <SiderContent>
                {/* <NwDivider>Invoice Details</NwDivider> */}
                <div className="confirmation-payment">
                    <div className="invoice-top-box">
                        <div className="invoice-top-box-icon">
                            <NwIcon
                                icon={byPrefixAndName.fasl['file-invoice']}
                                color={invoiceData.Number === null ? StyleVariables.DangerColor : '#000'}
                            />
                        </div>
                        {!invoiceData.Number ? (
                            <div className="invoice-top-box-content">
                                <div className="invoice-top-box-content-title">Not confirmed</div>
                            </div>
                        ) : (
                            <div className="invoice-top-box-content">
                                <div className="invoice-top-box-content-title">
                                    {invoiceData.Number} / {getMoment(invoiceData.Date).year()}{' '}
                                </div>
                                <div>{getMoment(invoiceData.Date).format('DD MMM YYYY')} </div>
                            </div>
                        )}
                    </div>
                    <div className="invoice-top-box">
                        <div className="invoice-top-box-icon">
                            <NwIcon
                                icon={selectedOption.icon}
                                opacity={!invoiceData.Number ? 0.25 : 1}
                                color={isOverDue() ? StyleVariables.DangerColor : '#000'}
                            />
                        </div>
                        <div className="invoice-top-box-content">
                            <div className="invoice-top-box-content-title">{selectedOption.label}</div>
                            {isPaid() && invoiceDataPaymentDate && (
                                <p>
                                    Payment date: <strong>{showDateNoTime(invoiceDataPaymentDate)}</strong>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {projects.length > 0 && (
                    <div className="projects-list">
                        <NwDivider>Project(s)</NwDivider>
                        <InvoiceProjectsList projects={projects} onProjectClick={handleOpenProjectDrawer} />
                    </div>
                )}
                {dataSource && dataSource.length > 0 && (
                    <div className="invoice-rows" style={{ marginRight: '.5rem' }}>
                        <NwDivider>Invoice Lines</NwDivider>
                        <Table showHeader={false} columns={columns} dataSource={dataSource} pagination={false} />
                    </div>
                )}
            </SiderContent>
            {projectPreviewID && <ProjectDrawer projectId={projectPreviewID} onClose={handleClosePreviewDrawer} />}
        </>
    );
};

export default CreditNotePageSider;
